import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from 'reactstrap';
import { SmlsListItem, SmlsModal } from '@smiles/smiles-ui-kit-react';
import { useSearch } from '../../hooks/search';

import { listOfClassTypes } from '../../common/initialItems';

import ISelectFlightClass from './types';

const SelectFlightClass: React.FC<ISelectFlightClass> = ({
  isMobile = false,
}: ISelectFlightClass) => {
  const { flightClass, setFlightClass, availableFlightClasses } = useSearch();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(
    listOfClassTypes[flightClass]
  );

  const [isOpenMobileModal, setIsOpenMobileModal] = useState(false);

  const { t } = useTranslation('flightSearch');

  const toggleMobileModal = useCallback(() => {
    setIsOpenMobileModal((prev) => !prev);
  }, []);

  const handleSelectItem = useCallback(
    (item) => {
      setSelectedItem(item);
      setFlightClass(item.value);
      if (isMobile) {
        toggleMobileModal();
      }
    },
    [isMobile, setFlightClass, toggleMobileModal]
  );

  useEffect(() => {
    if (availableFlightClasses.length === 1) {
      const itemWithDescription = listOfClassTypes[availableFlightClasses[0]];
      if (itemWithDescription) {
        setSelectedItem(itemWithDescription);
        setFlightClass(itemWithDescription.value);
      }
    }
  }, [availableFlightClasses, setFlightClass]);

  return !isMobile ? (
    <Dropdown
      isOpen={dropdownOpen}
      toggle={() => setDropdownOpen((prevState) => !prevState)}
      className="smls-dropdown-button"
    >
      <DropdownToggle id="drop_classType">
        {selectedItem.icon}
        {t(selectedItem.translationKey)}
        <i className="material-icons">
          {dropdownOpen ? 'arrow_drop_up' : 'arrow_drop_down'}
        </i>
      </DropdownToggle>
      <DropdownMenu>
        {availableFlightClasses.map((item) => (
          <DropdownItem
            onClick={() => handleSelectItem(listOfClassTypes[item])}
            key={listOfClassTypes[item].value}
            id={listOfClassTypes[item].id}
            className={[
              selectedItem.value === listOfClassTypes[item].value
                ? 'selected-item'
                : '',
              'align-text-left',
              availableFlightClasses.length === 1 ? 'is-single-item' : '',
            ].join(' ')}
          >
            {t(listOfClassTypes[item].translationKey)}
            {availableFlightClasses.length === 1 &&
              listOfClassTypes[item].description && (
                <span id="lbl_msgInformativeAvailableClasses">
                  {t(listOfClassTypes[item].description)}
                </span>
              )}
          </DropdownItem>
        ))}
      </DropdownMenu>
    </Dropdown>
  ) : (
    <>
      <SmlsListItem
        id="SelectFlightClass-search-class-selector"
        type="button"
        item={t('classType.mobileButtonLabel')}
        caption={t(selectedItem.translationKey)}
        className="smls-flight-search-class-selector"
        icon={<i className="material-icons">stars</i>}
        onClick={() => {
          setIsOpenMobileModal(true);
        }}
      />
      <SmlsModal
        isOpen={isOpenMobileModal}
        type="full"
        toggle={toggleMobileModal}
        className="smls-flight-class-selector-modal"
        defaultHeader={false}
      >
        <header className="custom-header">
          <button
            id="SelectFlightClass-mobile-modal"
            type="button"
            onClick={toggleMobileModal}
          >
            <i className="material-icons">chevron_left</i>
          </button>
        </header>
        <h3>{t('classType.mobileModalTitle')}</h3>

        <ul className="list-items">
          {availableFlightClasses.map((item) => (
            <li key={listOfClassTypes[item].value}>
              <button
                type="button"
                onClick={() => handleSelectItem(listOfClassTypes[item])}
              >
                {t(listOfClassTypes[item].translationKey)}
                {selectedItem.value === listOfClassTypes[item].value && (
                  <i className="material-icons">check</i>
                )}
              </button>
            </li>
          ))}
        </ul>
      </SmlsModal>
    </>
  );
};

export default SelectFlightClass;
